import { useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close-2.svg';
import { ReactComponent as ArrowLeftIcon } from '@elementary/common-ui/assets/arrow-left.svg';
import {
  Flex,
  FlexCenter,
  FlexSpace,
  IconButton,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import GenerateAIButton from '@elementary/common-ui/components/GenerateAIButton';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import { CircularProgress, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  DRAWER_TYPE,
  INCIDENT_ID,
  PARENT_TEST_ID,
  TEST_ID,
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_TEST_RESULTS,
} from '@elementary/common-ui/constants/urlParams';
import {
  OPEN_INCIDENTS,
  ACKNOWLEDGED_INCIDENTS,
  RESOLVED_INCIDENTS,
  INCIDENTS_OVERVIEW,
  INCIDENT_FILTERS,
  CLOUD_TESTS,
} from '@elementary/common-ui/constants/queryKeys';
import { useAssetData } from 'shared/hooks/useAssetData/useAssetData';
import useTestOverviewData from '../hooks/useTestOverviewData';
import { TestOverview } from '../types';
import AssetHeaderActions from '../../Asset/AssetHeaderActions';
import PanelActionsMenu from './PanelActionsMenu';
import ToggleDisableTestDialog from './ToggleDisableTestDialog';

const AssetActions = ({
  id,
  isTest,
  hideAIButton,
}: {
  id: string;
  isTest: boolean;
  hideAIButton?: boolean;
}) => {
  const { assetData, testData } = useAssetData({ id, isTest });
  return (
    <AssetHeaderActions
      asset={assetData}
      testData={testData}
      hideAIButton={hideAIButton}
    />
  );
};

const CloseIconStyled = styled(CloseIcon)`
  width: 14px;
  height: 14px;
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const BackArrowButton = styled(IconButton)`
  width: 16px;
  height: 16px;
  padding: 0;
  margin-right: 8px;

  svg {
    width: 16px;
    height: 16px;
    path {
      fill: ${({ theme }) => theme.palette.common.gray2};
    }
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

interface PanelHeaderProps {
  hideCloseButton?: boolean;
}

const PanelHeader = ({ hideCloseButton }: PanelHeaderProps) => {
  const [isToggleDisableDialogOpen, setIsToggleDisableDialogOpen] =
    useState(false);

  const { selectedId, sourceType, selectedDrawerType, closeSidePanel } =
    useSidePanelParams();
  const { data, error, invalidate } = useTestOverviewData();
  const { pollingTestsIds } = useTestsPolling();
  const queryClient = useQueryClient();
  const selectedEnvId = useSelectedEnv();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: account } = useAccount();
  const hasPermission = useEnvPermission();
  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');

  const parentTestId = searchParams.get(PARENT_TEST_ID);
  const isTestManagementContext =
    pathname.includes('/test-management') && !parentTestId;

  if (!['test', 'asset', 'cloud_test'].includes(selectedDrawerType))
    return null;

  const selectedTableAssetId = (data as TestOverview)?.test.tableAsset?.id;

  const dataName = (data as TestOverview)?.test.name;

  const isCloudTest =
    sourceType === 'test' &&
    isElementaryCloudTest((data as TestOverview)?.test.type);

  const isTestEnabled = !(
    sourceType === 'test' && (data as TestOverview)?.test.disabled
  );
  const isTestConfigUpdating = selectedId && pollingTestsIds?.has(selectedId);

  const isIncidentsPage = pathname.includes('/incidents');
  const incidentId = searchParams.get(INCIDENT_ID);
  const showInvestigateButton = !!(isIncidentsPage && incidentId);

  const handleUpdateSuccess = () => {
    if (isTestEnabled) {
      queryClient.invalidateQueries([OPEN_INCIDENTS]);
      queryClient.invalidateQueries([ACKNOWLEDGED_INCIDENTS]);
      queryClient.invalidateQueries([RESOLVED_INCIDENTS]);
      queryClient.invalidateQueries([INCIDENTS_OVERVIEW]);
      queryClient.invalidateQueries([INCIDENT_FILTERS]);
      queryClient.invalidateQueries([CLOUD_TESTS, selectedEnvId]);
    }
    invalidate();
  };

  const handleClose = () => {
    setIsToggleDisableDialogOpen(false);
    closeSidePanel();
  };

  const handleBackToIncident = () => {
    closeSidePanel();
  };

  const handleBackToLatestResults = () => {
    setSearchParams((prev) => {
      prev.set(TEST_ID, parentTestId!);
      prev.set(DRAWER_TYPE, 'test');
      prev.set(TEST_OVERVIEW_SIDEPANEL_TAB, TEST_OVERVIEW_TAB_TEST_RESULTS);
      prev.delete(PARENT_TEST_ID);
      return prev;
    });
  };

  return (
    <>
      <FlexSpace sx={{ width: '100%', mr: 1 }}>
        <Flex sx={{ gap: 1, alignItems: 'center', overflow: 'hidden' }}>
          {incidentId && (
            <Tooltip title="Back to Incident">
              <BackArrowButton onClick={handleBackToIncident}>
                <ArrowLeftIcon />
              </BackArrowButton>
            </Tooltip>
          )}
          {pathname.includes('/test-management') && parentTestId && (
            <Tooltip title="Back to Test configuration">
              <BackArrowButton onClick={handleBackToLatestResults}>
                <ArrowLeftIcon />
              </BackArrowButton>
            </Tooltip>
          )}
          <OverflowTooltip bold typographySx={{ fontSize: '18px' }}>
            {selectedDrawerType === 'asset'
              ? 'Asset Overview'
              : isTestManagementContext
              ? 'Test Configuration'
              : 'Test Overview'}
          </OverflowTooltip>
        </Flex>

        <FlexCenter gap={1}>
          {showInvestigateButton && (
            <GenerateAIButton
              suggestions={['Find the root cause', 'Show downstream impact']}
              context={incidentId ? { incidentIds: [incidentId] } : undefined}
              disabled={!account?.ai_features_enabled}
            >
              Investigate with AI
            </GenerateAIButton>
          )}

          {isTestConfigUpdating && (
            <Tooltip title="Applying test configuration changes">
              <CircularProgress size={14} />
            </Tooltip>
          )}

          <FlexCenter gap={1}>
            {!error && selectedDrawerType !== 'asset' && (
              <PanelActionsMenu
                tableAssetId={selectedTableAssetId}
                testName={dataName}
                columnName={
                  sourceType === 'test' &&
                  (data as TestOverview)?.execution.resource?.type === 'COLUMN'
                    ? (data as TestOverview)?.execution.resource?.name
                        ?.split('.')
                        .pop()
                    : undefined
                }
                showEnableDisable={isCloudTest}
                isTestEnabled={isTestEnabled}
                onToggleDisable={() => {
                  setIsToggleDisableDialogOpen(true);
                }}
                hasEditPermission={hasEditPermission}
              />
            )}
            {selectedDrawerType === 'asset' && selectedId && (
              <AssetActions
                id={selectedId}
                isTest={sourceType === 'test'}
                hideAIButton={!!incidentId}
              />
            )}

            {!hideCloseButton && (
              <IconButton onClick={closeSidePanel}>
                <CloseIconStyled />
              </IconButton>
            )}
          </FlexCenter>
        </FlexCenter>
      </FlexSpace>
      {sourceType === 'test' && (
        <ToggleDisableTestDialog
          open={isToggleDisableDialogOpen}
          testUniqueId={(data as TestOverview)?.test.id}
          testName={(data as TestOverview)?.test.name}
          onClose={handleClose}
          onSuccess={handleUpdateSuccess}
          mode={isTestEnabled ? 'disable' : 'enable'}
        />
      )}
    </>
  );
};

export default PanelHeader;
